import {
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  getRedirectResult,
  OAuthProvider,
  SAMLAuthProvider,
  signInWithEmailAndPassword,
  signInWithRedirect,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type User,
} from 'firebase/auth';
import { auth } from '../services/firebaseConfig';
import { resolveMfaChallenge } from './mfa';
import { jsonOrThrow, readApiError } from './apiError';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export interface SignInResult {
  user: User;
  activeOrgId: string | null;
  orgRoles: string[];
  platformRoles: string[];
}

/**
 * Sign-in either succeeds outright or returns a "needs MFA" branch that the
 * caller resolves via completeMfaSignIn(). Modeled as a discriminated union
 * (rather than throwing the multi-factor error) so the LoginPage can handle
 * the challenge as a normal UI state instead of catching exceptions.
 */
export type SignInOutcome =
  | { kind: 'success'; result: SignInResult }
  | { kind: 'mfa-required'; resolver: MultiFactorResolver; hints: MultiFactorInfo[] };

export async function lookupTenantBySlug(slug: string): Promise<{ gcipTenantId: string; name: string }> {
  const res = await fetch(`${API_URL}/v1/auth/tenant-by-slug/${encodeURIComponent(slug)}`);
  if (!res.ok) throw await readApiError(res);
  return res.json();
}

export async function signInToOrg(
  slug: string,
  email: string,
  password: string,
): Promise<SignInOutcome> {
  if (!auth) throw new Error('Firebase not configured');

  const { gcipTenantId } = await lookupTenantBySlug(slug);
  auth.tenantId = gcipTenantId;

  let user: User;
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    user = cred.user;
  } catch (err) {
    // The auth/multi-factor-auth-required error carries the resolver we need
    // to continue. Surface it to the caller as a typed branch instead of
    // letting it bubble — the UI handles MFA as a step, not an exception.
    const code = (err as { code?: string })?.code;
    if (code === 'auth/multi-factor-auth-required') {
      const resolver = getMultiFactorResolver(auth, err as MultiFactorError);
      // MP107 — Fire-and-forget beacon: the user just entered the
      // MFA challenge state. Feeds auth.mfa.challenged into the
      // policy + AI Insights pipeline. Errors silently swallowed —
      // observability shouldn't ever block UX.
      void postAuthBeacon({ kind: 'mfa_challenged', orgSlug: slug, email });
      return { kind: 'mfa-required', resolver, hints: resolver.hints };
    }
    // MP107 — Sign-in failed (not an MFA flow). Fire-and-forget
    // beacon with the Firebase auth code mapped to our canonical
    // taxonomy so admins can run DSL conditions on reasons like
    // 'too_many_attempts' without parsing free-form text.
    void postAuthBeacon({
      kind: 'signin_failed',
      orgSlug: slug,
      email,
      reason: firebaseAuthCodeToSigninFailedReason(code),
    });
    throw err;
  }

  const result = await mintClaimsAndRefresh(user);
  return { kind: 'success', result };
}

/** MP418 — Create an account INSIDE an org's GCIP tenant for someone who
 *  holds an invitation but has no account there yet. The invitation
 *  token is the authorisation (the accept page only offers this once
 *  GET /v1/invitations/by-token resolved), so this deliberately does
 *  not consult the public signup mode — under `invite_only` /signup is
 *  closed, and before this an invitee with no account had nowhere to go.
 *
 *  Mirrors signInToOrg: pin auth.tenantId, create the user, mint claims
 *  (activeOrgId comes back null — no membership yet — and the accept
 *  call creates it). `auth/email-already-in-use` means an account exists
 *  in THIS tenant: the caller tells the invitee to sign in instead. */
export async function createAccountInOrg(
  gcipTenantId: string,
  email: string,
  password: string,
): Promise<SignInResult> {
  if (!auth) throw new Error('Firebase not configured');
  auth.tenantId = gcipTenantId;
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  return mintClaimsAndRefresh(cred.user);
}

/** Step 2 of the MFA flow. Caller passes the resolver from `signInToOrg`'s
 *  mfa-required branch + the chosen factor uid + the user's TOTP code.
 *  Returns the same SignInResult shape as a normal sign-in. */
export async function completeMfaSignIn(
  resolver: MultiFactorResolver,
  factorUid: string,
  oneTimeCode: string,
  /** MP107 — Slug + email threaded through so the failure beacon
   *  can associate the event with the right org. Optional for
   *  backward compatibility; when absent the beacon is skipped. */
  beaconContext?: { slug: string; email: string },
): Promise<SignInResult> {
  try {
    const user = await resolveMfaChallenge(resolver, factorUid, oneTimeCode);
    return await mintClaimsAndRefresh(user);
  } catch (err) {
    if (beaconContext) {
      const code = (err as { code?: string })?.code;
      void postAuthBeacon({
        kind: 'mfa_failed',
        orgSlug: beaconContext.slug,
        email: beaconContext.email,
        reason: firebaseAuthCodeToMfaFailedReason(code),
      });
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// MP107 — Client beacon for unauthenticated auth events
// ---------------------------------------------------------------------------

interface PostAuthBeaconInput {
  kind: 'signin_failed' | 'mfa_challenged' | 'mfa_failed';
  orgSlug: string;
  email?: string;
  reason?: string;
}

/** Fire-and-forget POST to /v1/auth/events. Never throws — the
 *  beacon is observability for the policy + digest pipeline; a
 *  flaky network or rate-limit shouldn't impact the user's sign-in
 *  experience. The server-side handler also accepts anonymous calls
 *  (no auth token) since the user is mid-sign-in when these fire. */
function postAuthBeacon(input: PostAuthBeaconInput): Promise<void> {
  // Strip optional fields the server doesn't need so we don't send
  // `email: undefined` in the body (zod's optional + undefined is
  // fine but it keeps the request log clean).
  const body: Record<string, unknown> = { kind: input.kind, orgSlug: input.orgSlug };
  if (input.email) body.email = input.email;
  if (input.reason) body.reason = input.reason;
  return fetch(`${API_URL}/v1/auth/events`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    // keepalive flag lets the browser send the beacon even when the
    // page is unloading (e.g. user closes the tab right after the
    // sign-in failure renders).
    keepalive: true,
  })
    .then(() => undefined)
    .catch(() => undefined);
}

/** Map Firebase auth error codes to our canonical signin-failed
 *  reason taxonomy. Unknown codes pass through as `unknown` so the
 *  server emits a signal admins can still aggregate. */
function firebaseAuthCodeToSigninFailedReason(code: string | undefined): string {
  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'bad_credentials';
    case 'auth/user-disabled':
      return 'user_disabled';
    case 'auth/user-not-found':
      return 'user_not_found';
    case 'auth/too-many-requests':
      return 'too_many_attempts';
    default:
      return 'unknown';
  }
}

/** Map Firebase MFA error codes. The 'too-many-requests' code on the
 *  MFA endpoint indicates throttle, not the same as the sign-in one
 *  but we collapse to the same canonical taxonomy. */
function firebaseAuthCodeToMfaFailedReason(code: string | undefined): string {
  switch (code) {
    case 'auth/invalid-verification-code':
      return 'wrong_code';
    case 'auth/code-expired':
      return 'expired_code';
    case 'auth/too-many-requests':
      return 'too_many_attempts';
    default:
      return 'unknown';
  }
}

// ---------------------------------------------------------------------------
// SSO (SAML / OIDC) sign-in
// ---------------------------------------------------------------------------

export interface SsoProviderHint {
  providerId: string;
  kind: 'saml' | 'oidc';
  displayName: string;
}

export interface SsoDiscoveryResult {
  tenantId: string;
  slug: string;
  orgName: string;
  providers: SsoProviderHint[];
  /** When true, this org has force-SSO enabled — password sign-in is
   *  blocked server-side at /v1/auth/claims. The LoginPage uses this
   *  to hide the password fields entirely so the only path is one of
   *  the SSO buttons. */
  forceSsoOnly?: boolean;
}

/** Public lookup. Pass slug, email, or both — the API matches against
 *  org slug first, then `settings.allowedEmailDomains`. Throws on 404
 *  (treat as "no SSO available — fall back to password"). */
export async function discoverSso(
  input: { slug?: string; email?: string },
): Promise<SsoDiscoveryResult> {
  const res = await fetch(`${API_URL}/v1/auth/sso/discover`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return jsonOrThrow(res);
}

// Persisted across the IdP redirect so we know which org/tenant the user
// was signing into when the result comes back. Firebase restores tenantId
// internally for redirect results, but we still need the slug to display
// "Signing in to <Org>…" and to recover gracefully if the user opens the
// callback URL in a fresh tab.
const SSO_REDIRECT_KEY = 'sbaj.sso.redirect';
interface PendingRedirect {
  slug: string;
  tenantId: string;
  orgName: string;
  providerId: string;
  startedAt: number;
  /** Page to land on once the IdP sends the user back (already validated
   *  by lib/returnTo; re-validated on the way out). Kept here, not in the
   *  URL, so the target never travels through the IdP round-trip. */
  returnTo?: string | null;
}

export function getPendingSsoRedirect(): PendingRedirect | null {
  try {
    const raw = sessionStorage.getItem(SSO_REDIRECT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingRedirect;
  } catch {
    return null;
  }
}

export function clearPendingSsoRedirect(): void {
  try {
    sessionStorage.removeItem(SSO_REDIRECT_KEY);
  } catch {
    /* ignore */
  }
}

/** Kick off a SAML or OIDC sign-in. The browser leaves the SPA and lands at
 *  the IdP; on return Firebase processes the result and onAuthStateChanged
 *  fires with a User. The AuthContext picks it up and mints claims via
 *  finishSsoRedirect(). */
export async function signInWithSsoRedirect(
  discovery: SsoDiscoveryResult,
  providerId: string,
  returnTo: string | null = null,
): Promise<void> {
  if (!auth) throw new Error('Firebase not configured');
  auth.tenantId = discovery.tenantId;

  const provider = providerId.startsWith('saml.')
    ? new SAMLAuthProvider(providerId)
    : new OAuthProvider(providerId);

  const pending: PendingRedirect = {
    slug: discovery.slug,
    tenantId: discovery.tenantId,
    orgName: discovery.orgName,
    providerId,
    startedAt: Date.now(),
    returnTo,
  };
  try {
    sessionStorage.setItem(SSO_REDIRECT_KEY, JSON.stringify(pending));
  } catch {
    /* sessionStorage may be unavailable in private mode — proceed anyway */
  }

  await signInWithRedirect(auth, provider);
}

/** Called once on app boot. If the SDK has a pending redirect result, mint
 *  claims and return the SignInResult; otherwise null. Safe to call when
 *  there's nothing pending — Firebase resolves to null. */
export async function finishSsoRedirect(): Promise<SignInResult | null> {
  if (!auth) return null;
  let cred;
  try {
    cred = await getRedirectResult(auth);
  } catch (err) {
    clearPendingSsoRedirect();
    throw err;
  }
  if (!cred?.user) return null;
  clearPendingSsoRedirect();
  return mintClaimsAndRefresh(cred.user);
}

// ---------------------------------------------------------------------------

// Both sign-in paths converge here — POST to /v1/auth/claims to attach
// activeOrgId + orgRoles + platformRoles, then force-refresh the ID token
// so the new claims are immediately readable.
async function mintClaimsAndRefresh(user: User): Promise<SignInResult> {
  const idToken = await user.getIdToken();
  const res = await fetch(`${API_URL}/v1/auth/claims`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw await readApiError(res);
  const claims = await res.json();
  await user.getIdToken(true);
  return {
    user,
    activeOrgId: claims.activeOrgId,
    orgRoles: claims.orgRoles,
    platformRoles: claims.platformRoles,
  };
}
